"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createEntity,
  CreateEntityField,
  EntityRelationInput,
  updateEntity,
  UpdateEntityField,
} from "@/services/entity.service";
import { useCallback, useMemo } from "react";
import { useInvalidateSchemaCache } from "@/hooks/schema/use-invalidate-schema-cache";
import { useAuthenticatedQuery } from "@/hooks/use-authenticated-query";
import { useCustomerId } from "@/contexts/customer-id-context";
import {
  entitiesQuery,
  getLatestCachedEntities,
  schemaHashQuery,
  useSchemaHash,
} from "@/hooks/schema/schema-queries";

const itemsToFilter = [
  "entity",
  "entityfield",
  "pylouser",
  "entityrelation",
  "pylocustomer",
  "pyloaro",
  // stuff the admin panel needs
  "userprofile",
  "mainlayout",
  "customerconfig",
  "entityviewcomponentmapping",
  "pyloapikey",
  "pylomedia",
  "dashboardview",
  "entityview",
  "flownodemeta",
  "dashboard",
];

export const useEntitiesFromDb = () => {
  const queryClient = useQueryClient();
  const invalidateSchemaCache = useInvalidateSchemaCache();

  const customerId = useCustomerId();
  const schemaHash = useSchemaHash();

  // Until the current hash arrives (or while its list loads), show the newest list we have.
  // Disabled on public pages (login, join) via useAuthenticatedQuery
  const {
    data: entities,
    isFetched,
    isPlaceholderData,
  } = useAuthenticatedQuery({
    ...entitiesQuery(customerId, schemaHash),
    enabled: !!schemaHash,
    placeholderData: (previous) => previous ?? getLatestCachedEntities(queryClient, customerId),
  });
  // true once the list is known to match the current schema hash
  const entitiesFetched = isFetched && !isPlaceholderData;

  // All entities from the database, sorted alphabetically
  const allAvailableEntities = useMemo(() => {
    if (!entities) return undefined;
    return entities.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [entities]);

  const availableEntities = useMemo(() => {
    const result =
      allAvailableEntities
        ?.filter((entity) => {
          return (
            !(entity?.name && itemsToFilter.includes(entity.name.toLowerCase())) &&
            !entity?.is_system_entity
          );
        })
        .filter((entity) => !!entity && !entity.is_system_entity)
        .sort((a, b) => a?.name.localeCompare(b?.name ?? "") ?? 0) || [];

    return result;
  }, [allAvailableEntities]);

  // Read a single entity straight from the list query cache. Used after a save to
  // re-init local form state from the SAME object the dirty-compare effect uses as
  // its baseline (selection ENTITY_DATA_SELECTION), so deep-equal holds afterwards.
  // The hash is read at call time: right after a save, the render's hash is the old one.
  const getEntityFromCache = useCallback(
    (entityId: string) => {
      const currentHash = queryClient.getQueryData(schemaHashQuery(customerId).queryKey);
      return queryClient
        .getQueryData(entitiesQuery(customerId, currentHash).queryKey)
        ?.find((e) => e.id === entityId);
    },
    [queryClient, customerId],
  );

  const { mutateAsync: createEntityMutation, isPending: isCreatingEntity } = useMutation({
    mutationFn: async ({
      entityName,
      entityFields,
      entityRelations,
      isAro,
    }: {
      entityName: string;
      entityFields: Array<CreateEntityField>;
      entityRelations: Array<EntityRelationInput>;
      isAro: boolean;
    }) => {
      return await createEntity({
        entityName,
        entityFields,
        entityRelations,
        isAro,
      });
    },
    onSuccess: async () => {
      await invalidateSchemaCache();
    },
  });

  const { mutateAsync: updateEntityMutation, isPending: isUpdatingEntity } = useMutation({
    mutationFn: async ({
      entityId,
      entityName,
      entityFieldsToCreateOrUpdate,
      entityFieldIdsToDelete,
      entityRelationsToCreateOrUpdate,
      entityRelationIdsToDelete,
      isAro,
    }: {
      entityId: string;
      entityName?: string;
      entityFieldsToCreateOrUpdate: Array<UpdateEntityField | CreateEntityField>;
      entityFieldIdsToDelete: Array<string>;
      entityRelationsToCreateOrUpdate: Array<EntityRelationInput>;
      entityRelationIdsToDelete: Array<string>;
      isAro?: boolean;
    }) => {
      return await updateEntity({
        entityId,
        entityName,
        entityFieldsToCreateOrUpdate,
        entityFieldIdsToDelete,
        entityRelationsToCreateOrUpdate,
        entityRelationIdsToDelete,
        isAro,
      });
    },
    onSuccess: async () => {
      await invalidateSchemaCache();
    },
  });

  return {
    entities,
    entitiesFetched,
    availableEntities,
    allAvailableEntities,
    getEntityFromCache,
    createEntity: createEntityMutation,
    updateEntity: updateEntityMutation,
    isCreatingEntity,
    isUpdatingEntity,
  };
};
